import { txToast } from "@/components/TxToaster";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useOpenPromptDialog } from "@/hooks/dialogs";
import { useLiveConsoleBookmarks, useLiveConsoleHistory } from "@/pages/LiveConsole/liveConsoleHooks";
import { cn } from "@/lib/utils";
import { PlusIcon, StarIcon, StarOffIcon, XIcon } from "lucide-react";
import { HoverTip } from '@/components/ui/tooltip';


type SheetProps = {
    isOpen: boolean;
    closeSheet: () => void;
    toTermInput: (_cmd: string) => void;
}

function SheetBackdrop({ isOpen, closeSheet }: Omit<SheetProps, 'toTermInput'>) {
    return (
        <div
            className={cn(
                'absolute inset-0 z-20',
                'bg-void/80 duration-300',
                'data-[state=open]:pointer-events-auto data-[state=closed]:pointer-events-none',
                'data-[state=open]:opacity-100',
                'data-[state=closed]:opacity-0',
            )}
            data-state={isOpen ? 'open' : 'closed'}
            onClick={closeSheet}
        />
    )
}


function SheetCloseButton({ closeSheet }: Pick<SheetProps, 'closeSheet'>) {
    return (
        <HoverTip label="Close">
            <button
                className={cn(
                    'pressable size-9 shrink-0 rounded-tile cursor-pointer',
                    'flex items-center justify-center border border-transparent text-faint',
                    'transition-colors hover:border-edge hover:bg-tile hover:text-fg',
                    'focus:outline-none focus-visible:border-accent/45 focus-visible:text-fg',
                )}
                onClick={closeSheet}
                aria-label="Close"
            >
                <XIcon className="size-5" strokeWidth={2.25} />
            </button>
        </HoverTip>
    )
}


const sheetActionClasses = cn(
    'pressable w-full min-h-9 rounded-tile border',
    'flex items-center justify-center gap-2',
    'font-rad text-label font-semibold uppercase tracking-[.12em] scrim',
    'transition-colors mb-2',
);


type SheetCommandProps = {
    cmd: string;
    type: 'history' | 'saved';
    onClick: () => void;
    onFavAction: () => void;
}

function SheetCommand({ cmd, type, onClick, onFavAction }: SheetCommandProps) {
    const handleFavAction = (event: React.MouseEvent) => {
        event.stopPropagation();
        onFavAction();
    };

    return (
        <div
            onClick={onClick}
            className={cn(
                'group flex justify-between items-center gap-2 cursor-pointer',
                'px-2 py-1 rounded-bar border border-transparent text-dim',
                'transition-colors hover:border-edge hover:bg-tile hover:text-fg',
            )}
        >
            <span className="py-1 line-clamp-4 font-mono text-caption">
                {cmd}
            </span>
            <div className="min-w-max">
                <button
                    className={cn(
                        'pressable size-7 shrink-0 rounded-bar flex items-center justify-center',
                        'border border-transparent text-faint transition-colors',
                        'hover:border-amber/45 hover:bg-amber/10 hover:text-amber',
                        'invisible group-hover:visible',
                    )}
                    onClick={handleFavAction}
                >
                    {type === 'history' ? (<>
                        <StarIcon className="size-4" strokeWidth={2.25} />
                        <span className="sr-only">Save</span>
                    </>) : (<>
                        <StarOffIcon className="size-4" strokeWidth={2.25} />
                        <span className="sr-only">Remove</span>
                    </>)}
                </button>
            </div>
        </div >
    )
}


function SheetContent({ toTermInput }: Pick<SheetProps, 'toTermInput'>) {
    const { history, wipeHistory } = useLiveConsoleHistory();
    const { bookmarks, addBookmark, removeBookmark } = useLiveConsoleBookmarks();
    const openPromptDialog = useOpenPromptDialog();

    const handleWipeHistory = () => {
        txToast.success('History cleared');
        wipeHistory();
    }
    const handleSaveCommand = () => {
        openPromptDialog({
            title: 'Save Command',
            message: 'Enter the command to save:',
            submitLabel: 'Save',
            submitBtnVariant: 'success',
            onSubmit: (cmd) => {
                if (cmd) addBookmark(cmd);
            }
        })
    }
    return (
        <div className="flex flex-row gap-4 max-h-full">
            <div className="flex flex-col flex-grow gap-2 w-1/2">
                <h2 className="font-highlight text-caption uppercase tracking-[.14em] text-fg scrim">History</h2>
                <ScrollArea className="max-h-full w-full pr-5 text-caption text-dim" style={{ wordBreak: 'break-word' }}>
                    <button
                        onClick={handleWipeHistory}
                        className={cn(
                            sheetActionClasses,
                            'border-danger/45 bg-danger/10 text-danger-soft',
                            'hover:border-danger/70 hover:bg-danger/20',
                        )}
                    >
                        <XIcon className="size-4 shrink-0 drop-scrim" strokeWidth={2.25} />
                        Clear History
                    </button>
                    <div className="space-y-2 line-clamp-1 text-sm font-mono tracking-wide  pb-4">
                        {history.map((cmd, index) => (
                            <SheetCommand
                                key={index}
                                cmd={cmd}
                                type='history'
                                onClick={() => toTermInput(cmd)}
                                onFavAction={() => addBookmark(cmd)}
                            />
                        ))}
                    </div>
                    {history.length === 0 && (
                        <div className="w-full h-auto py-4 text-center text-faint">
                            The command history is empty.
                        </div>
                    )}
                </ScrollArea>
            </div>
            <div className="flex flex-col flex-grow gap-2 w-1/2">
                <h2 className="font-highlight text-caption uppercase tracking-[.14em] text-fg scrim">Saved</h2>
                <ScrollArea className="max-h-full w-full pr-5 text-caption text-dim" style={{ wordBreak: 'break-word' }}>
                    <button
                        onClick={handleSaveCommand}
                        className={cn(
                            sheetActionClasses,
                            'border-success/45 bg-success/10 text-success',
                            'hover:border-success/70 hover:bg-success/20',
                        )}
                    >
                        <PlusIcon className="size-4 shrink-0 drop-scrim" strokeWidth={2.25} />
                        Add New
                    </button>
                    <div className="space-y-2 line-clamp-1 text-sm font-mono tracking-wide  pb-4">
                        {bookmarks.map((cmd, index) => (
                            <SheetCommand
                                key={index}
                                cmd={cmd}
                                type='saved'
                                onClick={() => toTermInput(cmd)}
                                onFavAction={() => removeBookmark(cmd)}
                            />
                        ))}
                    </div>
                    {bookmarks.length === 0 && (
                        <div className="w-full h-auto py-4 text-center text-faint">
                            There are no saved commands. <br />
                            To save a command, click the star icon next to it.
                        </div>
                    )}
                </ScrollArea>
            </div>
        </div>
    )
}


export default function LiveConsoleSaveSheet({ isOpen, closeSheet, toTermInput }: SheetProps) {
    return <>
        <SheetBackdrop isOpen={isOpen} closeSheet={closeSheet} />
        <div
            data-state={isOpen ? 'open' : 'closed'}
            className={cn(
                'absolute z-20 inset-y-0 w-full md:max-w-2xl flex flex-col gap-3',
                'bg-void px-4 py-4 shadow-pop border-l border-edge',
                'data-[state=open]:pointer-events-auto data-[state=closed]:pointer-events-none',
                'transition-all duration-300 ease-in-out',
                isOpen ? 'right-0 opacity-100' : '-right-full opacity-0',
            )}
        >
            <div className="flex justify-end shrink-0">
                <SheetCloseButton closeSheet={closeSheet} />
            </div>
            <div className="min-h-0 grow">
                <SheetContent toTermInput={toTermInput} />
            </div>
        </div>
    </>;
}
